import React from 'react';
import { ArrowRight, CheckCircle2, LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';

export const MarketingContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn('mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10', className)} {...props} />;

interface SectionHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: 'left' | 'center';
  className?: string;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  eyebrow,
  title,
  description,
  align = 'center',
  className,
}) => (
  <div className={cn('max-w-3xl', align === 'center' && 'mx-auto text-center', className)}>
    {eyebrow && <p className="marketing-eyebrow">{eyebrow}</p>}
    <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
      {title}
    </h2>
    {description && (
      <p className="mt-5 text-lg leading-8 text-slate-600 dark:text-slate-300">{description}</p>
    )}
  </div>
);

type ButtonVariant = 'primary' | 'secondary' | 'ghost';

interface MarketingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  trailingArrow?: boolean;
}

export const MarketingButton: React.FC<MarketingButtonProps> = ({
  variant = 'primary',
  trailingArrow,
  className,
  children,
  ...props
}) => (
  <button
    className={cn(
      'marketing-focus inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition duration-200',
      variant === 'primary' &&
        'bg-blue-600 text-white shadow-[0_10px_30px_-12px_rgba(37,99,235,.8)] hover:bg-blue-700 hover:-translate-y-0.5',
      variant === 'secondary' &&
        'border border-slate-300 bg-white text-slate-900 shadow-sm hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800',
      variant === 'ghost' &&
        'text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
      className,
    )}
    {...props}
  >
    {children}
    {trailingArrow && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
  </button>
);

interface FeatureIconProps {
  icon: LucideIcon;
  tone?: 'blue' | 'violet' | 'emerald' | 'amber';
}

export const FeatureIcon: React.FC<FeatureIconProps> = ({ icon: Icon, tone = 'blue' }) => (
  <span
    className={cn(
      'inline-flex h-11 w-11 items-center justify-center rounded-xl',
      tone === 'blue' && 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
      tone === 'violet' && 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
      tone === 'emerald' && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
      tone === 'amber' && 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
    )}
  >
    <Icon className="h-5 w-5" aria-hidden="true" />
  </span>
);

export const CheckList: React.FC<{ items: string[]; className?: string }> = ({ items, className }) => (
  <ul className={cn('space-y-3', className)}>
    {items.map((item) => (
      <li key={item} className="flex gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);
